import React, { useState } from "react";
import { sbSetSetting, sbGetSetting } from "../utils/supabaseClient";
import { sendNotification } from "../utils/notifications";
import { createAssessment } from "../utils/assessmentUtils";
import AssessmentEditModal from "./AssessmentEditModal";
import AssessmentMarkdownImportModal from "./AssessmentMarkdownImportModal";
import { SVG_ICONS } from "./TablerIconPicker";
import "../styles/ListTab.css";

const AssessmentCreateTab = ({ onCreated }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });
  const [editing, setEditing] = useState(false);
  const [newAssessment, setNewAssessment] = useState(null);
  const [saveStatus, setSaveStatus] = useState("");
  const [showMarkdownImport, setShowMarkdownImport] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      alert("Assessment name is required");
      return;
    }

    try {
      const assessment = createAssessment(formData.name);
      assessment.description = formData.description;

      // Get existing assessments and add new one
      const existing = (await sbGetSetting("origamic-custom-assessments")) || [];
      const updated = [...existing, assessment];
      await sbSetSetting("origamic-custom-assessments", updated);

      setNewAssessment(assessment);
      setEditing(true);
    } catch (err) {
      console.error("Error creating assessment:", err);
      alert("Failed to create assessment");
    }
  };

  const handleEditSave = async (updatedAssessment) => {
    try {
      const existing = (await sbGetSetting("origamic-custom-assessments")) || [];
      const updated = existing.map((a) =>
        a.id === updatedAssessment.id ? updatedAssessment : a
      );
      await sbSetSetting("origamic-custom-assessments", updated);

      // Create notification if assessment is published
      if (updatedAssessment.live) {
        console.log("📢 Assessment published - creating notification");
        await sendNotification(
          `New Assessment: ${updatedAssessment.name}`,
          `${updatedAssessment.description || "Take this assessment to evaluate your AI readiness."}`,
          "assessment",
          updatedAssessment.id
        );
      }

      setSaveStatus("Assessment saved successfully!");
      setFormData({ name: "", description: "" });
      setEditing(false);
      setNewAssessment(null);

      if (onCreated) {
        onCreated();
      }

      setTimeout(() => setSaveStatus(""), 3000);
    } catch (err) {
      console.error("Error saving assessment:", err);
      alert("Failed to save assessment");
    }
  };

  const handleEditCancel = () => {
    // Remove the new assessment if cancelled
    setEditing(false);
    setNewAssessment(null);
  };

  const handleMarkdownImport = async (importedData) => {
    try {
      console.log("📥 Imported data from markdown:", importedData);
      console.log("📝 Questions count:", importedData.questions?.length);
      console.log("🎯 Score bands count:", importedData.scoreBands?.length);

      const assessment = createAssessment(importedData.name);
      assessment.description = importedData.description;
      assessment.questions = importedData.questions;
      assessment.scoreBands = importedData.scoreBands;
      assessment.showResults = importedData.showResults || "scoreAndAnswers";
      assessment.allowRetake = importedData.allowRetake !== false;
      assessment.version = importedData.version || "1.0";
      assessment.live = false; // Always import as draft

      console.log("📋 [CLIPBOARD] Assessment created with fields:", {
        name: assessment.name,
        description: assessment.description,
        questionsCount: assessment.questions?.length,
        scoreBandsCount: assessment.scoreBands?.length,
        fullAssessment: assessment
      });

      // Get existing assessments and add new one
      const existing = (await sbGetSetting("origamic-custom-assessments")) || [];
      const updated = [...existing, assessment];
      await sbSetSetting("origamic-custom-assessments", updated);

      setNewAssessment(assessment);
      setShowMarkdownImport(false);
      setEditing(true);
      console.log("Assessment imported and saved:", assessment);
    } catch (err) {
      console.error("Error importing assessment:", err);
      alert("Failed to import assessment: " + err.message);
    }
  };

  if (editing && newAssessment) {
    return (
      <AssessmentEditModal
        assessment={newAssessment}
        onSave={handleEditSave}
        onCancel={handleEditCancel}
      />
    );
  }

  if (showMarkdownImport) {
    return (
      <AssessmentMarkdownImportModal
        onImport={handleMarkdownImport}
        onCancel={() => setShowMarkdownImport(false)}
      />
    );
  }

  return (
    <div className="edit-form" style={{ width: "100%", maxWidth: "100%", margin: "20px 0" }}>
      <h3>Create New Assessment</h3>

      <div className="form-group">
        <label>Assessment Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="e.g., AI Readiness Assessment"
        />
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Brief description of what this assessment measures"
          rows="4"
        />
      </div>

      <div className="form-actions">
        <button
          className="btn-save"
          onClick={handleCreate}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
        >
          + Create & Edit
        </button>
        <button
          className="btn-cancel"
          onClick={() => setShowMarkdownImport(true)}
          style={{ background: "#6366F1", color: "white", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}
        >
          {SVG_ICONS["notes"]({ size: 16, color: "white" })}
          Import from Markdown
        </button>
      </div>

      {saveStatus && (
        <div style={{
          marginTop: "16px",
          padding: "12px",
          background: "#22C55E20",
          color: "#22C55E",
          borderRadius: "6px",
          fontSize: "14px",
          textAlign: "center"
        }}>
          {saveStatus}
        </div>
      )}
    </div>
  );
};

export default AssessmentCreateTab;
